import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import ResponseDataView from './ResponseData.view';
import useGrouping from 'Common/Utils/Hooks/useGrouping';
import errorCb from 'Common/Utils/errorCb';
import { fetchAppointeeResponses } from '../AppointeeResponse.service';
import useDataSource from 'Common/Utils/Hooks/useDataSource';

const emptyArray = [];

function ResponseData() {
    const grouping = useGrouping();
    const [filterOpen, setFilterOpen] = useState(true);
    const [isFiltering, setIsFiltering] = useState(true);
    const [refinerOpen, setRefinerOpen] = useState(false);
    const [selectedYear, setSelectedYear] = useState('');
    const [{ data }, setDataSource] = useDataSource();
    const [displayData, setDisplayData] = useState();
    const [exportDataSource, setExportDataSource] = useState();
    const [exportDataSourceHorizontal, setExportDataSourceHorizontal] = useState();
    const [selected, setSelected] = useState();
    const globalDispatch = useDispatch();
    const appointeeErrorCallback = useMemo(() => errorCb([], "Error fetching appointee responses.", globalDispatch), [globalDispatch]);

    const handleFilterToggle = useCallback(() => setFilterOpen(prev => !prev), []);

    const handleRefinerToggle = useCallback(() => setRefinerOpen(prev => !prev), []);

    const handleFilterSubmit = useCallback((year, types, teamIds) => {
        const promise = fetchAppointeeResponses(year, grouping, types, teamIds, appointeeErrorCallback);
        setDataSource(promise);
        setExportDataSource(() => () => promise.then(result => flattenData(result)));
        setExportDataSourceHorizontal(() => () => promise.then(result => flattenDataHorizontal(result)));
        setDisplayData(promise);
        setFilterOpen(false);
        setIsFiltering(false);
        setRefinerOpen(true);
    }, [appointeeErrorCallback, grouping, setDataSource]);

    const handleFilterClear = useCallback(() => {
        setDataSource(null);
        setExportDataSource(null);
        setExportDataSourceHorizontal(null);
        setDisplayData(null);
        setFilterOpen(true);
        setIsFiltering(true);
        setRefinerOpen(false);
    }, [setDataSource]);

    const handleRefinementChange = useCallback(refined => {
        setDisplayData(refined);
        setExportDataSource(() => () => flattenData(refined));
        setExportDataSourceHorizontal(() => () => flattenDataHorizontal(refined));
    }, []);

    if (!grouping) {
        return null;
    }

    return (
        <ResponseDataView
            displayData={displayData}
            exportDataSource={exportDataSource}
            filterOpen={filterOpen}
            isFiltering={isFiltering}
            refinerOpen={refinerOpen}
            grouping={grouping}
            selected={selected}
            showData={!!displayData}
            sourceData={data || emptyArray}
            title={`${grouping} Appointee Response`}
            year={selectedYear}
            onFilterClear={handleFilterClear}
            onFilterSubmit={handleFilterSubmit}
            onFilterToggle={handleFilterToggle}
            onFilteringChange={setIsFiltering}
            onRefinementChange={handleRefinementChange}
            onRefinerToggle={handleRefinerToggle}
            onSelectionChange={setSelected}
            onYearChange={setSelectedYear}
            exportDataSourceHorizontal={exportDataSourceHorizontal}
        />
    );
}

function flattenData(data) {
    const results = [];
    for (const row of data) {
        const flatRow = {
            ...row,
        };
        delete flatRow.additionalInfo;

        let entries = null;
        if (row.additionalInfo) {
            entries = Object.entries(row.additionalInfo);
        }

        if (entries?.length) {
            for (const [question, answer] of Object.entries(row.additionalInfo)) {
                const resultRow = {
                    ...flatRow,
                    question,
                    answer,
                };

                results.push(resultRow);
            }
        } else {
            results.push(flatRow);
        }
    }

    return results;
}

function flattenDataHorizontal(data) {
    const results = [];
    for (const row of data) {
        const resultRow = {
            ...row,
        };
        delete resultRow.additionalInfo;

        let entries = null;
        if (row.additionalInfo) {
            entries = Object.entries(row.additionalInfo);
        }

        let count = 0;
        if (entries?.length) {
            for (const [question, answer] of Object.entries(row.additionalInfo)) {
                resultRow['q'.concat(count + 1)] = question;
                resultRow['a'.concat(count + 1)] = answer;
                count++;
            }
        }
        resultRow['numberOfQuestions'] = count;
        results.push(resultRow);
    }
    return results;
}

export default ResponseData;